<form
  class="address-form-field"
  [formGroup]="addressForm"
  [attr.autocomplete]="'on'"
  [class.address-form-field--expanded]="
    isExpanded() || (addressForm.invalid && addressForm.dirty)
  "
  [class.address-form-field--collapsed]="!(isExpanded() && addressForm.invalid)"
>
  <mat-form-field class="address-field__address-line-1" fzFullWidth>
    <mat-label>
      <ng-content select="mat-label, label">Address line 1</ng-content>
    </mat-label>

    <div class="address-field__address-line-1__wrapper">
      <input
        #addressLine1
        type="text"
        matInput
        formControlName="addressLine1"
        oninput="this.value = this.value.replace(/(^\w{1})|(\s+\w{1})/g, letter => letter.toUpperCase());"
        class="address-field__input"
        [attr.autocomplete]="'address-line1'"
        [attr.required]="required"
        [attr.readonly]="disabled"
        [attr.disabled]="disabled"
        [controlErrorAnchor]="addressAnchor"
        (focus)="handleFocus()"
        (input)="valueChangedFor('addressLine1', $event)"
      />

      <!-- this input it used to show the full address -->
      @if (!isExpanded() && !addressForm.invalid) {
        <input
          matInput
          class="address-field__address-line-1__wrapper__full_address"
          disabled
          tabindex="-1"
          [value]="address().fullAddress"
        />
      }
    </div>

    <mat-hint>
      <ng-content select="mat-hint"></ng-content>
    </mat-hint>

    <!-- Field Error -->
    <mat-error>
      <ng-template
        controlErrorAnchor
        #addressAnchor="controlErrorAnchor"
      ></ng-template>
    </mat-error>
  </mat-form-field>

  <!-- Partial address form -->
  <div class="address-form-field__components">
    <!-- Address secondary field -->
    <mat-form-field class="address-form-field__components__address-line-2">
      <mat-label translate="form-field.address-line-2.label">
        Apartment, suite, etc.
      </mat-label>
      <input
        matInput
        formControlName="secondary"
        class="address-field__input"
        [attr.autocomplete]="'address-line2'"
        [attr.required]="required"
        [attr.readonly]="disabled"
        [attr.disabled]="disabled"
      />
    </mat-form-field>

    <!-- Address city field -->
    <mat-form-field class="address-form-field__components__city">
      <mat-label translate="form-field.city.label">City</mat-label>
      <input
        matInput
        formControlName="city"
        class="address-field__input"
        [attr.autocomplete]="'address-level2'"
        [attr.required]="required"
        [attr.readonly]="disabled"
        [attr.disabled]="disabled"
        [matAutocomplete]="addressCity"
        [controlErrorAnchor]="addressCityAnchor"
        (input)="valueChangedFor('city', $event)"
      />

      <mat-autocomplete #addressCity="matAutocomplete">
        @for (city of cities$ | async; track $index) {
          <mat-optgroup
            class="address-field__option-group"
            [label]="city.label"
          >
            @for (name of city.options; track $index) {
              <mat-option class="address-field__option" [value]="name">
                <span
                  class="address-field__option__value"
                  [innerHTML]="
                    name
                      | highlight
                        : (addressForm.controls.city.value
                            ? addressForm.controls.city.value
                            : '')
                  "
                ></span>
              </mat-option>
            }
          </mat-optgroup>
        }
      </mat-autocomplete>
      <!-- Field Error -->
      <mat-error>
        <ng-template
          controlErrorAnchor
          #addressCityAnchor="controlErrorAnchor"
        ></ng-template>
      </mat-error>
    </mat-form-field>

    <!-- Address state field -->
    <mat-form-field class="address-form-field__components__state">
      <mat-label translate="form-field.state.label">State</mat-label>
      <input
        matInput
        formControlName="state"
        class="address-field__input"
        [attr.autocomplete]="'address-level1'"
        [attr.required]="required"
        [attr.readonly]="disabled"
        [attr.disabled]="disabled"
        [matAutocomplete]="autoGroup"
        [controlErrorAnchor]="addressStateAnchor"
        (input)="valueChangedFor('state', $event)"
      />
      <mat-autocomplete #autoGroup="matAutocomplete" autoActiveFirstOption>
        @for (state of states$ | async; track $index) {
          <mat-option class="address-field__option" [value]="state.value">
            <span
              class="address-field__option__value"
              [innerHTML]="
                state.label | highlight: addressForm.controls.state.value ?? ''
              "
            ></span>
          </mat-option>
        }
      </mat-autocomplete>

      <!-- Field Error -->
      <mat-error>
        <ng-template
          controlErrorAnchor
          #addressStateAnchor="controlErrorAnchor"
        ></ng-template>
      </mat-error>
    </mat-form-field>

    <!-- Address zip-code field -->
    <mat-form-field class="address-form-field__components__zip-code">
      <mat-label translate="form-field.zip-code.label">ZipCode</mat-label>
      <input
        matInput
        formControlName="zipCode"
        placeholder="e.g. 12345"
        mask="99999-9999"
        class="address-field__input"
        [attr.autocomplete]="'postal-code'"
        [attr.required]="required"
        [attr.readonly]="disabled"
        [attr.disabled]="disabled"
        [attr.minlength]="5"
        [attr.maxlength]="10"
        [pattern]="'[0-9]*'"
        [controlErrorAnchor]="addressZipCodeAnchor"
        (input)="valueChangedFor('zipCode', $event)"
      />
      <mat-error>
        <ng-template
          controlErrorAnchor
          #addressZipCodeAnchor="controlErrorAnchor"
        ></ng-template>
      </mat-error>
    </mat-form-field>
  </div>

  @if (showHint()) {
    <small class="mat-caption-small font-color--tertiary">
      Hint: Always double check your address is written like you see it in your
      ID or driver’s license.
    </small>
  }
</form>
